import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { httpRequest } from "../../utils/httpRequest";
import { X, Plus, Trash2 } from "lucide-react";
import CustomEditor from "../common/Editor";
import ExplanationField from "./ExplanationField";

function replaceClozePlaceholders(html, startIndex = 1) {
  if (typeof html !== "string") return { text: "", count: 0 };
  let i = startIndex - 1;
  // می‌گیرد: [], [ ], [blank], [ تهی ], [anyword]
  const rx =
    /\[\s*(?:blank|تهی|EMPTY|empty|فراغ|BLANK|[A-Za-z\u0600-\u06FF_]*)?\s*\]/g;
  const text = html.replace(rx, () => {
    i += 1;
    return `{{${i}}}`;
  });
  return { text, count: i - (startIndex - 1) };
}

const makeEmptyForm = () => ({
  kind: "cloze_text",
  stem_fa: "",
  stem_en: "",
  text_fa: "",
  text_en: "",
  clozeAnswers: [
    { key: "1", answers_fa: "", answers_en: "", caseSensitive: false },
  ],
  layout: "top-bottom",
  points: 1,
  status: "published",
  tags: "",
  explanation_fa: "",
  explanation_en: "",
});

const itemToForm = (item) => {
  if (!item) return makeEmptyForm();

  const kind = item.kind || "cloze_text";
  const stem_fa = item?.stem?.fa || "";
  const stem_en = item?.stem?.en || "";

  const body = item.body || {};
  const text_fa = body?.text?.fa || "";
  const text_en = body?.text?.en || "";

  const blanks = Array.isArray(body.blanks) ? body.blanks : [];
  const clozeAnswers =
    blanks.length > 0
      ? blanks
          .slice()
          .sort((a, b) => Number(a.key) - Number(b.key))
          .map((b) => ({
            key: String(b.key ?? ""),
            answers_fa: Array.isArray(b.answers?.fa)
              ? b.answers.fa.join(", ")
              : "",
            answers_en: Array.isArray(b.answers?.en)
              ? b.answers.en.join(", ")
              : "",
            caseSensitive: !!b.caseSensitive,
          }))
      : [{ key: "1", answers_fa: "", answers_en: "", caseSensitive: false }];

  const layout = body.layout || "top-bottom";
  const points = item.points ?? 1;
  const status = item.status || "published";
  const tags = Array.isArray(item.tags) ? item.tags.join(",") : "";
  const explanation_fa = item?.explanation?.fa || "";
  const explanation_en = item?.explanation?.en || "";

  return {
    kind,
    stem_fa,
    stem_en,
    text_fa,
    text_en,
    clozeAnswers,
    layout,
    points,
    status,
    tags,
    explanation_fa,
    explanation_en,
  };
};

export default function ClozeModal({
  isOpen,
  onClose,
  token,
  onCreated,
  mode = "create", // "create" | "edit"
  item = null,
}) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language?.includes("fa");
  const isEdit = mode === "edit" && item;

  const [form, setForm] = useState(makeEmptyForm());

  const handleEditorChange = (field, content) => {
    setForm((f) => ({ ...f, [field]: content }));
  };

  useEffect(() => {
    if (!isOpen) return;
    if (isEdit) {
      setForm(itemToForm(item));
    } else {
      setForm(makeEmptyForm());
    }
  }, [isOpen, isEdit, item]);

  const addClozeAnswer = () => {
    setForm((f) => ({
      ...f,
      clozeAnswers: [
        ...f.clozeAnswers,
        {
          key: String(f.clozeAnswers.length + 1),
          answers_fa: "",
          answers_en: "",
          caseSensitive: false,
        },
      ],
    }));
  };

  const removeClozeAnswer = (idx) => {
    setForm((f) => ({
      ...f,
      clozeAnswers: f.clozeAnswers.filter((_, i) => i !== idx),
    }));
  };

  const handleAnswerChange = (idx, field, value) => {
    setForm((f) => {
      const answers = [...f.clozeAnswers];
      answers[idx] = { ...answers[idx], [field]: value };
      return { ...f, clozeAnswers: answers };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.stem_fa.trim() && !form.stem_en.trim()) {
      toast.error(t("cloze.toasts.stem_required"));
      return;
    }
    const validAnswers = form.clozeAnswers.filter(
      (a) => a.answers_fa.trim() || a.answers_en.trim()
    );
    if (validAnswers.length === 0) {
      toast.error(t("cloze.toasts.answers_required"));
      return;
    }
    if (form.text_fa.trim() === "" && form.text_en.trim() === "") {
      toast.error(t("cloze.toasts.passage_required"));
      return;
    }

    const faConv = replaceClozePlaceholders(form.text_fa, 1);
    const enConv = replaceClozePlaceholders(form.text_en, 1);

    const blanksCount = Math.max(faConv.count, enConv.count);

    if (blanksCount === 0) {
      toast.error(
        t("cloze.toasts.no_placeholders") ||
          "هیچ جای‌خالی‌ای در متن نیست (از [] استفاده کن)."
      );
      return;
    }

    const normalizedBlanks = Array.from({ length: blanksCount }, (_, idx) => {
      const a = form.clozeAnswers?.[idx];
      const key = String(idx + 1);

      const faList = (a?.answers_fa || "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);

      const enList = (a?.answers_en || "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);

      return {
        key,
        answers: { fa: faList, en: enList },
        caseSensitive: !!a?.caseSensitive,
      };
    });

    const payload = {
      kind: form.kind,
      explanation: {
        fa: form.explanation_fa || undefined,
        en: form.explanation_en || undefined,
      },
      stem: {
        fa: form.stem_fa || undefined,
        en: form.stem_en || undefined,
      },
      body: {
        text: {
          fa: faConv.text || undefined,
          en: enConv.text || undefined,
        },
        layout: form.layout || "top-bottom",
        blanks: normalizedBlanks,
      },
      points: Number(form.points) || 1,
      status: form.status || "draft",
      tags: form.tags
        ? form.tags
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean)
        : [],
    };

    try {
      const isEditMode = isEdit && item?._id;
      const url = isEditMode ? `/item/${item._id}` : "/item";
      const method = isEditMode ? "PUT" : "POST";

      const res = await httpRequest(method, url, payload, {
        "x-access-token": token,
      });
      const doc = res?.data?.data || res?.data;
      if (!doc?._id) throw new Error(res?.message || "FAILED");

      toast.success(
        isEditMode
          ? t("cloze.toasts.updated", "سؤال کلوز با موفقیت ویرایش شد")
          : t("cloze.toasts.created")
      );
      onCreated?.(doc);
      onClose?.();
    } catch (err) {
      toast.error(err?.message || t("common.unknown_error"));
    }
  };
  if (!isOpen) return null;

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        {" "}
        <h2 className="text-lg md:text-xl font-semibold">
          {t("cloze.title")}
        </h2>{" "}
        <button className="p-2 rounded-lg hover:bg-gray-100" onClick={onClose}>
          <X />{" "}
        </button>{" "}
      </div>{" "}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Question Type Selector */}{" "}
        <div className="flex flex-col md:flex-row gap-4 mb-4">
          {" "}
          <div className="flex-1">
            {" "}
            <label className="block text-sm font-medium mb-1">
              {t("cloze.select_type")}{" "}
            </label>{" "}
            <div className="flex items-center gap-4 border p-3 rounded-lg bg-gray-50">
              {" "}
              <div className="flex items-center">
                {" "}
                <input
                  type="radio"
                  name="kind"
                  id="cloze_text"
                  value="cloze_text"
                  checked={form.kind === "cloze_text"}
                  onChange={(e) => setForm({ ...form, kind: e.target.value })}
                  className="h-4 w-4 text-purple-600 focus:ring-purple-500"
                />{" "}
                <label
                  htmlFor="cloze_text"
                  className="ml-2 text-sm text-gray-700"
                >
                  {t("cloze.type_text")}{" "}
                </label>{" "}
              </div>{" "}
              <div className="flex items-center">
                {" "}
                <input
                  type="radio"
                  name="kind"
                  id="cloze_paragraph"
                  value="cloze_paragraph"
                  checked={form.kind === "cloze_paragraph"}
                  onChange={(e) => setForm({ ...form, kind: e.target.value })}
                  className="h-4 w-4 text-purple-600 focus:ring-purple-500"
                />{" "}
                <label
                  htmlFor="cloze_paragraph"
                  className="ml-2 text-sm text-gray-700"
                >
                  {t("cloze.type_paragraph")}{" "}
                </label>{" "}
              </div>{" "}
            </div>{" "}
          </div>{" "}
        </div>
        {/* Layout Selector */}{" "}
        <div>
          {" "}
          <label className="block text-sm font-medium mb-1">
            {t("cloze.layout")}
          </label>{" "}
          <div className="flex items-center flex-wrap gap-4 border p-3 rounded-lg bg-gray-50">
            {" "}
            {["top-bottom", "bottom-top", "left-right", "right-left"].map(
              (layout) => (
                <div key={layout} className="flex items-center">
                  {" "}
                  <input
                    type="radio"
                    id={`layout-${layout}`}
                    name="layout"
                    value={layout}
                    checked={form.layout === layout}
                    onChange={(e) =>
                      setForm({ ...form, layout: e.target.value })
                    }
                    className="h-4 w-4 text-purple-600 focus:ring-purple-500"
                  />{" "}
                  <label
                    htmlFor={`layout-${layout}`}
                    className="ml-2 text-sm text-gray-700"
                  >
                    {" "}
                    {t(`cloze.layout_question_answer_${layout}`)}{" "}
                  </label>{" "}
                </div>
              )
            )}{" "}
          </div>{" "}
        </div>
        {/* Stem Fields (Rich Text) */}{" "}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {" "}
          <div dir={isRtl ? "rtl" : "ltr"}>
            {" "}
            <label className="block text-sm font-medium mb-1">
              {t("cloze.stem_fa")}{" "}
            </label>{" "}
            <CustomEditor
              value={form.stem_fa}
              onEditorChange={(content) =>
                handleEditorChange("stem_fa", content)
              }
              placeholder={t("cloze.stem_fa_ph")}
              directionality={isRtl ? "rtl" : "ltr"}
            />{" "}
          </div>{" "}
          <div dir="ltr">
            {" "}
            <label className="block text-sm font-medium mb-1">
              {t("cloze.stem_en")}{" "}
            </label>{" "}
            <CustomEditor
              value={form.stem_en}
              onEditorChange={(content) =>
                handleEditorChange("stem_en", content)
              }
              placeholder={t("cloze.stem_en_ph")}
            />{" "}
          </div>{" "}
        </div>
        {/* Passage Fields (Rich Text) */}{" "}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {" "}
          <div dir={isRtl ? "rtl" : "ltr"}>
            {" "}
            <label className="block text-sm font-medium mb-1">
              {t("cloze.passage_fa")}{" "}
            </label>{" "}
            <CustomEditor
              value={form.text_fa}
              onEditorChange={(content) =>
                handleEditorChange("text_fa", content)
              }
              placeholder={t("cloze.passage_fa_ph")}
              directionality={isRtl ? "rtl" : "ltr"}
            />{" "}
          </div>{" "}
          <div dir="ltr">
            {" "}
            <label className="block text-sm font-medium mb-1">
              {t("cloze.passage_en")}{" "}
            </label>{" "}
            <CustomEditor
              value={form.text_en}
              onEditorChange={(content) =>
                handleEditorChange("text_en", content)
              }
              placeholder={t("cloze.passage_en_ph")}
            />{" "}
          </div>{" "}
        </div>
        {/* Cloze Answers */}{" "}
        <div>
          {" "}
          <div className="flex items-center justify-between mb-1">
            {" "}
            <label className="block text-sm font-medium">
              {t("cloze.blanks")}{" "}
            </label>{" "}
            <button
              type="button"
              onClick={addClozeAnswer}
              className="text-blue-600 text-sm"
            >
              {t("cloze.add_blank")}{" "}
            </button>{" "}
          </div>{" "}
          <div className="space-y-2">
            {" "}
            {form.clozeAnswers.map((answer, idx) => (
              <div
                key={idx}
                className="border rounded-lg p-3 space-y-2 bg-gray-50"
              >
                {" "}
                <div className="flex justify-between items-center">
                  {" "}
                  <span className="text-xs font-semibold">
                    {" "}
                    {t("cloze.blank_key", { key: answer.key })}{" "}
                  </span>{" "}
                  {form.clozeAnswers.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeClozeAnswer(idx)}
                      className="p-1 rounded-full text-red-600 hover:bg-red-100"
                    >
                      <Trash2 size={16} />{" "}
                    </button>
                  )}{" "}
                </div>{" "}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {" "}
                  <input
                    type="text"
                    placeholder={t("cloze.answer_fa_ph")}
                    value={answer.answers_fa}
                    onChange={(e) =>
                      handleAnswerChange(idx, "answers_fa", e.target.value)
                    }
                    className="w-full border rounded-lg px-3 py-2"
                  />{" "}
                  <input
                    type="text"
                    placeholder={t("cloze.answer_en_ph")}
                    value={answer.answers_en}
                    onChange={(e) =>
                      handleAnswerChange(idx, "answers_en", e.target.value)
                    }
                    className="w-full border rounded-lg px-3 py-2"
                  />{" "}
                </div>{" "}
                {form.kind === "cloze_paragraph" && (
                  <div className="flex items-center mt-2">
                    {" "}
                    <input
                      type="checkbox"
                      id={`caseSensitive-${idx}`}
                      checked={answer.caseSensitive}
                      onChange={(e) =>
                        handleAnswerChange(
                          idx,
                          "caseSensitive",
                          e.target.checked
                        )
                      }
                      className="rounded text-purple-600 focus:ring-purple-500"
                    />{" "}
                    <label
                      htmlFor={`caseSensitive-${idx}`}
                      className="ml-2 text-sm text-gray-700"
                    >
                      {t("cloze.case_sensitive")}{" "}
                    </label>{" "}
                  </div>
                )}{" "}
              </div>
            ))}{" "}
          </div>{" "}
        </div>
        {/* Meta Fields */}{" "}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {" "}
          <div>
            {" "}
            <label className="block text-sm font-medium mb-1">
              {t("cloze.points")}{" "}
            </label>{" "}
            <input
              type="number"
              min={0}
              className="w-full border rounded-lg px-3 py-2"
              value={form.points}
              onChange={(e) => setForm({ ...form, points: e.target.value })}
            />{" "}
          </div>{" "}
          <div>
            {" "}
            <label className="block text-sm font-medium mb-1">
              {t("cloze.status")}{" "}
            </label>{" "}
            <select
              className="w-full border rounded-lg px-3 py-2"
              value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value })}
            >
              {" "}
              <option value="published">{t("common.published")}</option>
              <option value="draft">{t("common.draft")}</option>{" "}
            </select>{" "}
          </div>{" "}
          <div>
            {" "}
            <label className="block text-sm font-medium mb-1">
              {t("cloze.tags")}{" "}
            </label>{" "}
            <input
              className="w-full border rounded-lg px-3 py-2"
              placeholder={t("common.tags_csv")}
              value={form.tags}
              onChange={(e) => setForm({ ...form, tags: e.target.value })}
            />{" "}
          </div>{" "}
        </div>{" "}
        <ExplanationField
          valueFa={form.explanation_fa}
          valueEn={form.explanation_en}
          onChangeFa={(v) => handleEditorChange("explanation_fa", v)}
          onChangeEn={(v) => handleEditorChange("explanation_en", v)}
          isRtl={isRtl}
        />
        <div className="mt-4">
          {" "}
          <button
            type="submit"
            className="px-4 py-2 bg-blue-600 text-white rounded-lg inline-flex items-center gap-2"
          >
            <Plus size={18} />{" "}
            {isEdit ? t("common.update") : t("common.create")}
          </button>{" "}
        </div>{" "}
      </form>{" "}
    </>
  );
}
